/**
 * Wave 6 SEO cluster (GSC-driven): filter rejection, no callback, InfoJobs file, salary.
 * Spanish and English. Merged from posts.js. Mid CTA variant `mid`.
 */

export default [
  {
    slug: 'cv-no-pasa-el-filtro',
    date: '2026-09-01',
    category: 'ats',
    related: [
      'errores-ats-que-descartan-tu-cv',
      'cv-canva-y-ats',
      'mejorar-el-cv-que-ya-tienes',
    ],
    es: {
      title: 'Mi CV no pasa el filtro: 7 causas en el archivo (no en tu experiencia)',
      description:
        'Tu currículum no pasa el filtro ATS y no es por falta de experiencia. Siete fallos del PDF (columnas, Canva, texto imagen) y cómo comprobarlo gratis antes de enviar.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: '“Mi CV no pasa el filtro” casi nunca significa que no vales para el puesto. Significa que el archivo que subiste no dejó texto legible para el software de selección. El ATS no lee tu carrera: lee caracteres. Si el PDF es una foto disfrazada, si las columnas rompen el orden, o si el título del puesto no aparece en el cuerpo, el sistema te marca bajo aunque tengas diez años de experiencia. Esta guía va al archivo, no a tu trayectoria.',
        },
        {
          type: 'p',
          text: 'Antes de reescribir la vida entera, comprueba si el problema es técnico. Abre el PDF, selecciona texto con el ratón. Si no se marca nada, el filtro ya te ha descartado antes de que un humano abra el correo. Hermanas: [errores ATS](/blog/errores-ats-que-descartan-tu-cv), [CV de Canva](/blog/cv-canva-y-ats) y [mejorar el CV que ya tienes](/blog/mejorar-el-cv-que-ya-tienes).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) analiza el PDF que subes y te dice si el texto es parseable. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: '1. PDF imagen o escaneado' },
        {
          type: 'p',
          text: 'Exportaste desde Canva, Photoshop o un escáner y el resultado es una página bonita sin capa de texto. El ATS ve un rectángulo gris. Solución: regenera desde Word o Google Docs, o usa una plantilla A4 de una columna y vuelve a exportar. No arregles con OCR casero si puedes evitarlo: suele dejar errores en emails y fechas.',
        },
        { type: 'h2', text: '2. Dos columnas que mezclan secciones' },
        {
          type: 'p',
          text: 'Experiencia a la izquierda y formación a la derecha suena bien en pantalla. Para el parser, a veces lee “Universidad de… Gerente de ventas 2019” en la misma línea. Una columna no es feo si la densidad está bien. Ver [CV a dos columnas](/blog/cv-a-dos-columnas).',
        },
        { type: 'h2', text: '3. Encabezados raros o iconos en lugar de texto' },
        {
          type: 'p',
          text: '“Experiencia” escrito con un icono de maletín, tablas para alinear fechas, o secciones llamadas “Mi trayectoria” en lugar de “Experiencia laboral” confunden al matching de palabras clave. Usa títulos estándar en texto plano.',
        },
        { type: 'h2', text: '4. Keywords de la oferta ausentes (sin mentir)' },
        {
          type: 'p',
          text: 'Si el anuncio pide “Power BI” y tú solo pones “informes”, el score baja. No inventes herramientas: reformula viñetas con el vocabulario del puesto cuando sea verdad. Método en [adaptar CV a una oferta](/blog/adaptar-cv-a-una-oferta) y [palabras clave ATS](/blog/palabras-clave-cv-ats).',
        },
        { type: 'h2', text: '5. Archivo corrupto, pesado o con contraseña' },
        {
          type: 'p',
          text: 'Más de 2–3 MB, PDF protegido o generado con una app que el portal no acepta: el upload falla en silencio o queda en cola eterna. Comprime sin bajar a imagen. Ver [peso del PDF](/blog/peso-cv-pdf).',
        },
        { type: 'h2', text: '6. Nombre de archivo genérico y duplicados' },
        {
          type: 'p',
          text: '“CV.pdf” no mata el ATS, pero en portales con miles de candidatos sí te hace invisible para el reclutador humano que busca tu apellido en la carpeta. Usa cv-nombre-apellido-puesto.pdf. Ver [nombre del archivo](/blog/nombre-archivo-cv).',
        },
        { type: 'h2', text: '7. Formato pedido ignorado' },
        {
          type: 'p',
          text: 'Piden Word y mandas PDF escaneado, o al revés. Algunos ATS de grandes retailers solo parsean DOCX. Lee el anuncio. [PDF vs Word](/blog/cv-pdf-vs-word).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '¿El filtro te rechaza antes de leer tu experiencia?',
          lead: 'Sube el PDF que ya tienes. En dos minutos ves si el texto es seleccionable y qué secciones el ATS no lee.',
          from: 'PDF bonito, cero texto',
          to: 'Archivo que pasa el parser',
          button: 'Comprobar mi CV gratis',
        },
        { type: 'h2', text: 'Checklist de 5 minutos antes de volver a enviar' },
        {
          type: 'ul',
          items: [
            'Seleccionar texto en el PDF: ¿funciona en todas las páginas?',
            '¿Una columna o columnas probadas con copiar-pegar a bloc de notas?',
            '¿Aparecen 5–8 términos del anuncio en experiencia o resumen?',
            '¿Peso < 2 MB y sin contraseña?',
            '¿Mismo idioma que la oferta ([inglés o español](/blog/cv-ingles-o-espanol))?',
          ],
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿El ATS descarta por la foto?',
              a: 'A veces la foto en cabecera rompe el orden del parseo. En mercados sin foto obligatoria, quítala si sospechas. Ver foto en el CV.',
            },
            {
              q: '¿Puedo “hacer trampa” al ATS?',
              a: 'Texto blanco con keywords es detectado y quema credibilidad. Mejor adaptar viñetas reales.',
            },
            {
              q: '¿ClarCV garantiza que me llamen?',
              a: 'No. Garantiza que el archivo sea legible y que veas qué mejorar antes del envío.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'My CV does not pass the filter: 7 file causes (not your experience)',
      description:
        'Your résumé fails the ATS filter — often not for lack of experience. Seven PDF issues (columns, Canva, image text) and how to check free before you apply.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: '“My CV does not pass the filter” rarely means you are unqualified. It means the file you uploaded did not leave readable text for screening software. The ATS does not read your career story — it reads characters. If the PDF is a disguised image, columns scramble order, or the job title never appears in the body, the system scores you low even with ten years of experience. This guide targets the file, not your track record.',
        },
        {
          type: 'p',
          text: 'Before rewriting your whole life, check whether the problem is technical. Open the PDF and try to select text. If nothing highlights, the filter rejected you before a human opened the email. Related: [ATS mistakes](/blog/errores-ats-que-descartan-tu-cv), [Canva CVs](/blog/cv-canva-y-ats), [improve the CV you have](/blog/mejorar-el-cv-que-ya-tienes).',
        },
        {
          type: 'p',
          text: 'ClarCV (clarcv.com) analyses the PDF you upload and shows whether text is parseable. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: '1. Image or scanned PDF' },
        {
          type: 'p',
          text: 'You exported from Canva, Photoshop, or a scanner and got a pretty page with no text layer. The ATS sees a grey rectangle. Fix: regenerate from Word or Google Docs, or use a single-column A4 template and export again.',
        },
        { type: 'h2', text: '2. Two columns that mix sections' },
        {
          type: 'p',
          text: 'Experience on the left and education on the right looks fine on screen. Parsers sometimes read “University of… Sales Manager 2019” on one line. One column is not ugly if density is right. See [two-column CVs](/blog/cv-a-dos-columnas).',
        },
        { type: 'h2', text: '3. Odd headings or icons instead of text' },
        {
          type: 'p',
          text: '“Experience” as a suitcase icon, tables for dates, or sections titled “My journey” instead of “Work experience” break keyword matching. Use standard headings in plain text.',
        },
        { type: 'h2', text: '4. Missing job keywords (without lying)' },
        {
          type: 'p',
          text: 'If the ad asks for “Power BI” and you only write “reports”, the score drops. Do not invent tools — rephrase bullets with the job vocabulary when true. See [tailor to the job](/blog/adaptar-cv-a-una-oferta).',
        },
        { type: 'h2', text: '5. Corrupt, heavy, or password-protected file' },
        {
          type: 'p',
          text: 'Over 2–3 MB, locked PDFs, or files from apps the portal rejects fail silently. Compress without turning pages into images. See [PDF weight](/blog/peso-cv-pdf).',
        },
        { type: 'h2', text: '6. Generic filename' },
        {
          type: 'p',
          text: '“CV.pdf” does not always break the ATS, but it hides you from recruiters searching folders. Use cv-firstname-lastname-role.pdf. See [file naming](/blog/nombre-archivo-cv).',
        },
        { type: 'h2', text: '7. Ignoring the requested format' },
        {
          type: 'p',
          text: 'They ask for Word and you send a scanned PDF, or the opposite. Some enterprise ATS only parse DOCX. Read the ad. [PDF vs Word](/blog/cv-pdf-vs-word).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Rejected before anyone reads your experience?',
          lead: 'Upload the PDF you already have. In two minutes see if text is selectable and which sections the ATS misses.',
          from: 'Pretty PDF, zero text',
          to: 'File the parser can read',
          button: 'Check my CV free',
        },
        { type: 'h2', text: '5-minute checklist before resubmitting' },
        {
          type: 'ul',
          items: [
            'Select text in the PDF — does it work on every page?',
            'One column, or columns tested with copy-paste to Notepad?',
            'Do 5–8 terms from the ad appear in experience or summary?',
            'Under 2 MB and no password?',
            'Same language as the job ad?',
          ],
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does the ATS reject because of a photo?',
              a: 'Sometimes a header photo breaks parse order. In markets where photos are optional, remove it if you suspect issues.',
            },
            {
              q: 'Can I cheat the ATS?',
              a: 'White-on-white keywords get flagged and burn trust. Adapt real bullets instead.',
            },
            {
              q: 'Does ClarCV guarantee a callback?',
              a: 'No. It shows whether the file is readable and what to fix before you apply.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'por-que-no-me-llaman-cv',
    date: '2026-09-01',
    category: 'career',
    related: [
      'cv-no-pasa-el-filtro',
      'seguimiento-despues-de-enviar-cv',
      'cv-para-portales-empleo',
    ],
    es: {
      title: 'Envío el CV y no me llaman: archivo, ATS y portal (no solo experiencia)',
      description:
        '¿Envías el currículum y no te llaman? Causas del archivo, del portal y del timing — no solo “falta de experiencia”. Qué comprobar antes del siguiente envío.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Enviar el CV y no recibir llamada es frustrante porque asumes que el problema eres tú: poca experiencia, edad, sector. A veces sí. Muchas veces el CV nunca llegó legible al reclutador: portal que no subió el adjunto, PDF de dos columnas, oferta con 400 candidatos en 48 horas y un filtro automático al 20 %. Esta guía separa lo que controlas (archivo, adaptación, seguimiento) de lo que no (timing del cierre, internal hire).',
        },
        {
          type: 'p',
          text: 'No es la misma historia que [CV no pasa el filtro](/blog/cv-no-pasa-el-filtro): aquí el síntoma es silencio después del envío. Puede ser rechazo técnico, rechazo por fit, o simplemente que aún no han abierto la bandeja. Hermanas: [seguimiento](/blog/seguimiento-despues-de-enviar-cv) y [portales de empleo](/blog/cv-para-portales-empleo).',
        },
        {
          type: 'p',
          text: 'ClarCV no te consigue entrevistas mágicas. Te ayuda a no enviar un archivo que muere en el parser. Gratis: 3 análisis / 30 días, sin tarjeta.',
        },
        { type: 'h2', text: 'Silencio por archivo (la causa que no ves)' },
        {
          type: 'p',
          text: 'InfoJobs, Indeed, LinkedIn Easy Apply y los ATS de grandes empresas guardan tu PDF y extraen campos. Si la extracción falla, tu perfil queda medio vacío aunque el adjunto se vea bien en tu móvil. Síntomas: en el portal tu experiencia aparece recortada o sin fechas. Solución: una columna, texto seleccionable, [checklist antes de enviar](/blog/checklist-cv-antes-de-enviar).',
        },
        { type: 'h2', text: 'Silencio por volumen y timing' },
        {
          type: 'p',
          text: 'Ofertas con 300 inscripciones en un fin de semana: el ATS ordena por keyword match y fecha. Enviar el mismo PDF genérico el domingo noche compite con quien adaptó el resumen el lunes a las 9:00. No garantiza llamada, pero sube probabilidad. [Adaptar en 20 minutos](/blog/adaptar-cv-a-una-oferta).',
        },
        { type: 'h2', text: 'Silencio por canal equivocado' },
        {
          type: 'p',
          text: 'Candidatura solo por portal cuando el anuncio pide email directo, o al revés. Algunas ETT priorizan perfiles ya en su base. Si la oferta es de Randstad o Adecco, el adjunto importa tanto como el portal. Ver guías de [archivo para portales](/blog/cv-para-portales-empleo).',
        },
        { type: 'h2', text: 'Silencio por expectativas irreales de respuesta' },
        {
          type: 'p',
          text: 'Plazos típicos: 1–2 semanas en procesos medianos, más en administración pública, cero respuesta si la oferta se congeló. Un seguimiento educado a los 7–10 días está bien; tres correos en 48 horas no. Ver [seguimiento después de enviar](/blog/seguimiento-despues-de-enviar-cv).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '¿Y si el problema es el PDF, no tu perfil?',
          lead: 'Antes de la próxima ronda de envíos, comprueba que el texto se lee y que las keywords de la oferta están en el cuerpo.',
          from: 'Envíos sin respuesta',
          to: 'Archivo listo para el portal',
          button: 'Analizar mi CV gratis',
        },
        { type: 'h2', text: 'Qué hacer esta semana (orden práctico)' },
        {
          type: 'ul',
          items: [
            'Abre el último PDF enviado: ¿se selecciona texto?',
            'Compara con la oferta: ¿5 términos clave aparecen en experiencia o resumen?',
            'Revisa en el portal si tu perfil importado tiene huecos.',
            'Reenvía solo a ofertas nuevas o adaptadas, no spam del mismo archivo.',
            'Un seguimiento corto si pasaron 10 días y tienes contacto directo.',
          ],
        },
        { type: 'h2', text: 'Cuándo sí es experiencia o mercado' },
        {
          type: 'p',
          text: 'Si el archivo está limpio, adaptaste tres ofertas seguidas del mismo rol y sigue el silencio, puede ser seniority, salario fuera de banda, o sector en pausa. Ahí ayuda networking y ampliar tipo de puesto, no la décima plantilla de Canva.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Cuántos envíos sin respuesta son “normales”?',
              a: 'Depende del sector. En retail masivo, decenas de silencios con archivo correcto son habituales. El archivo correcto evita descartes evitables, no la competencia.',
            },
            {
              q: '¿Llamar por teléfono ayuda?',
              a: 'En pymes a veces. En portales con 500 candidatos, rara vez. Mejor email breve con el asunto de la oferta.',
            },
            {
              q: '¿Debo cambiar de plantilla cada vez?',
              a: 'Solo si la actual es ilegible para ATS. El contenido pesa más que el color.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'I send my CV and get no callback: file, ATS, and portal (not just experience)',
      description:
        'You apply and hear nothing back? File, portal, and timing causes — not only “lack of experience”. What to check before the next batch of applications.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Sending a CV and getting no call is frustrating because you assume the problem is you: thin experience, age, industry. Sometimes it is. Often the résumé never reached the recruiter in readable form: portal upload failed, two-column PDF, 400 applicants in 48 hours and an automatic cut at 20%. This guide separates what you control (file, tailoring, follow-up) from what you do not (hiring freeze, internal hire).',
        },
        {
          type: 'p',
          text: 'Different from [CV does not pass the filter](/blog/cv-no-pasa-el-filtro): here the symptom is silence after applying. It can be technical rejection, poor fit, or they simply have not opened the inbox yet. See [follow-up](/blog/seguimiento-despues-de-enviar-cv) and [job boards](/blog/cv-para-portales-empleo).',
        },
        {
          type: 'p',
          text: 'ClarCV does not magically get you interviews. It helps you stop sending files that die in the parser. Free: 3 analyses / 30 days, no card.',
        },
        { type: 'h2', text: 'Silence because of the file (the invisible cause)' },
        {
          type: 'p',
          text: 'InfoJobs, Indeed, LinkedIn Easy Apply, and enterprise ATS store your PDF and extract fields. When extraction fails, your profile looks half empty even if the attachment looks fine on your phone. Fix: single column, selectable text, [pre-send checklist](/blog/checklist-cv-antes-de-enviar).',
        },
        { type: 'h2', text: 'Silence because of volume and timing' },
        {
          type: 'p',
          text: 'Ads with 300 applications over a weekend: ATS ranks by keyword match and date. Sending the same generic PDF Sunday night competes with someone who tailored the summary Monday at 9 a.m. See [tailor in 20 minutes](/blog/adaptar-cv-a-una-oferta).',
        },
        { type: 'h2', text: 'Silence because of the wrong channel' },
        {
          type: 'p',
          text: 'Applying only on the portal when the ad asks for direct email, or the opposite. Some staffing firms prioritise profiles already in their database.',
        },
        { type: 'h2', text: 'Silence because of unrealistic response expectations' },
        {
          type: 'p',
          text: 'Typical timelines: 1–2 weeks for mid-size processes, longer in public sector, zero response if the role froze. One polite follow-up at 7–10 days is fine; three emails in 48 hours is not.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'What if the PDF is the problem, not your profile?',
          lead: 'Before the next application round, check that text is readable and job keywords appear in the body.',
          from: 'Applications with no reply',
          to: 'File ready for the portal',
          button: 'Analyse my CV free',
        },
        { type: 'h2', text: 'What to do this week (practical order)' },
        {
          type: 'ul',
          items: [
            'Open the last PDF you sent — can you select text?',
            'Compare to the ad — do 5 key terms appear in experience or summary?',
            'Check the portal — does the imported profile have gaps?',
            'Apply again only to new or tailored roles, not spam the same file.',
            'One short follow-up after 10 days if you have a direct contact.',
          ],
        },
        {
          type: 'faq',
          items: [
            {
              q: 'How many no-replies are “normal”?',
              a: 'Depends on the sector. In high-volume retail, dozens of silences with a correct file are common. A correct file avoids avoidable rejections, not competition.',
            },
            {
              q: 'Does calling help?',
              a: 'Sometimes at small companies. Rarely on portals with 500 applicants. A brief email with the job reference works better.',
            },
            {
              q: 'Should I change template every time?',
              a: 'Only if the current one is ATS-illegible. Content matters more than colour.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-infojobs-archivo',
    date: '2026-09-01',
    category: 'career',
    related: [
      'cv-para-portales-empleo',
      'enviar-cv-por-email',
      'peso-cv-pdf',
    ],
    es: {
      title: 'Archivo de CV para InfoJobs: PDF, nombre, peso y columnas',
      description:
        'Cómo adjuntar el CV en InfoJobs: formato PDF, nombre del archivo, peso máximo y por qué las columnas rompen el perfil importado. Guía del adjunto, no del buscador.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'InfoJobs tiene dos cosas distintas: el buscador de ofertas y el archivo que adjuntas a cada candidatura. Esta guía es del archivo. No repetimos “cómo buscar empleo en InfoJobs”. El PDF que subes al portal se guarda en tu perfil y a veces se reutiliza en inscripciones rápidas. Si ese archivo es un CV de Canva de dos columnas o un escaneado, el reclutador ve tu foto bonita y el ATS interno ve poco texto útil.',
        },
        {
          type: 'p',
          text: 'La guía general de portales está en [CV para portales de empleo](/blog/cv-para-portales-empleo). Aquí: formato, nombre, peso y prueba del bloc de notas específica para InfoJobs. También [enviar por email](/blog/enviar-cv-por-email) cuando la oferta pide correo directo además del clic en “Inscribirse”.',
        },
        {
          type: 'p',
          text: 'ClarCV genera un PDF A4 de una columna parseable. Renómbralo antes de subirlo a InfoJobs. Gratis: 3 análisis y 2 descargas / 30 días.',
        },
        { type: 'h2', text: 'PDF sí; imagen disfrazada no' },
        {
          type: 'p',
          text: 'InfoJobs acepta PDF y a veces Word según el flujo. El PDF debe tener texto seleccionable. Prueba: abre el fichero, Ctrl+A. Si no se marca nada, cambia de plantilla antes de inscribirte. [Canva y ATS](/blog/cv-canva-y-ats) explica el caso más frecuente en España.',
        },
        { type: 'h2', text: 'Una columna para que el importador no mezcle bloques' },
        {
          type: 'p',
          text: 'Tras subir el CV, revisa tu perfil en InfoJobs: ¿experiencia y fechas aparecen completas? Si faltan meses o puestos, el parser perdió el orden. Pasa a diseño de una columna. [CV a dos columnas](/blog/cv-a-dos-columnas) cuando insistir en dos vías.',
        },
        { type: 'h2', text: 'Nombre del archivo que encuentran en la descarga' },
        {
          type: 'p',
          text: 'Cuando el reclutador descarga candidatos, verá cientos de “curriculum.pdf”. Usa cv-nombre-apellido-puesto.pdf en minúsculas y guiones. Detalle en [nombre del archivo](/blog/nombre-archivo-cv).',
        },
        { type: 'h2', text: 'Peso y páginas' },
        {
          type: 'p',
          text: 'Mantén el PDF por debajo de 2 MB si puedes. CV de tres páginas con imágenes de certificados embebidas fallan en móvil con datos lentos. Comprime sin convertir el cuerpo en JPG. Ver [peso del CV PDF](/blog/peso-cv-pdf) y [longitud](/blog/longitud-del-cv).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '¿Tu InfoJobs muestra medio perfil vacío?',
          lead: 'Sube el mismo PDF a ClarCV y comprueba si el texto es legible antes de la próxima inscripción masiva.',
          from: 'Adjunto que no importa bien',
          to: 'PDF que el portal lee',
          button: 'Comprobar el archivo',
        },
        { type: 'h2', text: 'Carta y mensaje (no sustituyen al archivo)' },
        {
          type: 'p',
          text: 'El campo de presentación suma si es corto y cita el puesto. No pegues ahí lo que debería ir en viñetas del CV. El adjunto sigue siendo la prueba. [Candidatura espontánea](/blog/candidatura-espontanea-cv) si escribes sin oferta concreta.',
        },
        { type: 'h2', text: 'Checklist InfoJobs en 3 minutos' },
        {
          type: 'ul',
          items: [
            'Texto seleccionable en todo el PDF.',
            'Una columna o columnas probadas con copiar-pegar.',
            'Nombre: cv-apellido-rol.pdf.',
            'Peso razonable; sin contraseña.',
            'Perfil en InfoJobs revisado tras la subida: ¿fechas y puestos completos?',
            'Mismo idioma que el anuncio.',
          ],
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Actualizo el CV en cada oferta?',
              a: 'El contenido sí si cambian keywords importantes. El formato no hace falta si ya es parseable.',
            },
            {
              q: '¿Foto en InfoJobs?',
              a: 'La foto de perfil del portal es aparte del PDF. En el archivo, la foto grande en cabecera puede estorbar al ATS del PDF.',
            },
            {
              q: '¿Puedo tener varios CV en InfoJobs?',
              a: 'Depende de la versión del flujo; lo seguro es un PDF maestro limpio y variantes guardadas en tu disco con nombres distintos.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV file for InfoJobs: PDF, filename, size, and columns',
      description:
        'How to attach your résumé on InfoJobs: PDF format, filename, file size, and why columns break the imported profile. Attachment guide, not job search tips.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'InfoJobs has two different things: the job search and the file you attach to each application. This guide is about the file. We do not repeat “how to find jobs on InfoJobs”. The PDF you upload is stored on your profile and often reused for quick applications. If that file is a two-column Canva design or a scan, the recruiter sees a pretty layout while internal parsing gets little useful text.',
        },
        {
          type: 'p',
          text: 'General portal guidance: [CV for job boards](/blog/cv-para-portales-empleo). Here: format, name, size, and Notepad test specific to InfoJobs.',
        },
        {
          type: 'p',
          text: 'ClarCV generates a single-column, parseable A4 PDF. Rename it before uploading to InfoJobs. Free: 3 analyses and 2 downloads / 30 days.',
        },
        { type: 'h2', text: 'PDF yes; disguised image no' },
        {
          type: 'p',
          text: 'InfoJobs accepts PDF and sometimes Word depending on the flow. The PDF must have selectable text. Test: open the file, Select All. If nothing highlights, change template before applying.',
        },
        { type: 'h2', text: 'One column so import does not scramble blocks' },
        {
          type: 'p',
          text: 'After upload, check your InfoJobs profile — are experience and dates complete? Missing months or roles mean the parser lost order. Switch to single-column layout.',
        },
        { type: 'h2', text: 'Filename recruiters find in downloads' },
        {
          type: 'p',
          text: 'When recruiters download applicants, they see hundreds of “curriculum.pdf”. Use cv-firstname-lastname-role.pdf in lowercase with hyphens.',
        },
        { type: 'h2', text: 'Size and page count' },
        {
          type: 'p',
          text: 'Keep the PDF under 2 MB when possible. Three-page CVs with embedded certificate images fail on slow mobile data. Compress without turning body text into JPG.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Does InfoJobs show half an empty profile?',
          lead: 'Upload the same PDF to ClarCV and check whether text is readable before your next batch of applications.',
          from: 'Attachment that imports poorly',
          to: 'PDF the portal can read',
          button: 'Check the file',
        },
        { type: 'h2', text: 'Cover note (does not replace the file)' },
        {
          type: 'p',
          text: 'The presentation field helps if it is short and names the role. Do not paste what should be CV bullets. The attachment remains the proof.',
        },
        { type: 'h2', text: '3-minute InfoJobs checklist' },
        {
          type: 'ul',
          items: [
            'Selectable text across the PDF.',
            'One column, or columns tested with copy-paste.',
            'Filename: cv-lastname-role.pdf.',
            'Reasonable size; no password.',
            'InfoJobs profile reviewed after upload — complete dates and roles?',
            'Same language as the ad.',
          ],
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I update the CV for every ad?',
              a: 'Content yes when important keywords change. Format no if it is already parseable.',
            },
            {
              q: 'Photo on InfoJobs?',
              a: 'Portal profile photo is separate from the PDF. A large header photo in the file can hurt PDF parsing.',
            },
            {
              q: 'Can I keep several CVs on InfoJobs?',
              a: 'Depends on the flow version; safest is one clean master PDF and variants on disk with different names.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'pretensiones-salariales-en-el-cv',
    date: '2026-09-01',
    category: 'writing',
    related: [
      'que-no-poner-en-el-cv',
      'adaptar-cv-a-una-oferta',
      'candidatura-espontanea-cv',
    ],
    es: {
      title: 'Pretensiones salariales en el CV: cuándo ponerlas y cuándo no',
      description:
        '¿Poner salario o pretensiones salariales en el currículum? Cuándo conviene, cuándo te descartan y dónde escribirlo si el portal lo exige. Sin números inventados.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'Las pretensiones salariales en el CV generan dos errores opuestos: poner un número aleatorio “para no quedar mal” o esconderlo cuando el formulario lo exige y dejar el campo vacío. En España y la mayoría de Europa occidental, el salario en el currículum no es obligatorio y muchas veces resta: te ancla bajo si pediste poco, te filtra fuera si pediste alto, y no negocias con información de mercado que aún no tienes. Esta guía dice cuándo va en el PDF, cuándo solo en el portal, y cuándo no va en ningún sitio.',
        },
        {
          type: 'p',
          text: 'Complementa [qué no poner en el CV](/blog/que-no-poner-en-el-cv). Si adaptas cada envío, el salario rara vez es lo que cambia el match — las keywords sí. Ver [adaptar CV a una oferta](/blog/adaptar-cv-a-una-oferta).',
        },
        {
          type: 'p',
          text: 'ClarCV no rellena pretensiones salariales. Trabaja el archivo para que pase ATS. Gratis: 3 análisis / 30 días, sin tarjeta.',
        },
        { type: 'h2', text: 'Regla general: fuera del PDF' },
        {
          type: 'p',
          text: 'En el documento currículum (PDF o Word) no suele ir bruto “30.000 € brutos”. Ocupa espacio, envejece mal y no distingue fijo de variable. Excepciones más abajo. Si el portal tiene campo “pretensión salarial”, ahí sí respondes con un rango razonable investigado, no en la cabecera del CV.',
        },
        { type: 'h2', text: 'Cuándo el portal o la ETT lo exigen' },
        {
          type: 'p',
          text: 'Randstad, Adecco, InfoJobs y algunos ATS corporativos piden rango en el formulario. No copies ese número al PDF. Usa un rango basado en fuentes públicas (convenio, Glassdoor orientativo, ofertas similares), redondeado. “Negociable según paquete” es válido si el campo lo permite.',
        },
        { type: 'h2', text: 'Cuándo SÍ puede ir (pocas veces)' },
        {
          type: 'p',
          text: 'Candidatura espontánea a pyme sin portal estructurado y el dueño pidió explícitamente “CV con expectativa”. Una línea bajo contacto: “Expectativa: 28–32 k€ brutos/año, negociable”. Sin tablas ni letra gigante. [Candidatura espontánea](/blog/candidatura-espontanea-cv).',
        },
        { type: 'h2', text: 'Errores que te descartan' },
        {
          type: 'ul',
          items: [
            'Salario actual en el CV cuando buscas más: te encasilla.',
            '“Según convenio” en sectores sin convenio claro: parece evasiva.',
            'Rango del doble del mercado sin seniority que lo soporte.',
            'Mezclar neto y bruto sin decir cuál es.',
            'Poner salario en el resumen profesional: lo primero que leen y lo primero que filtra.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El salario no arregla un PDF que no leen',
          lead: 'Si te preocupa el filtro, primero comprueba que el archivo es legible. Luego negocia desde un CV que llega a RR. HH.',
          from: 'CV con número mal puesto',
          to: 'Archivo limpio + rango solo donde toca',
          button: 'Revisar mi CV gratis',
        },
        { type: 'h2', text: 'Dónde negociar en su lugar' },
        {
          type: 'p',
          text: 'Entrevista telefónica o primera ronda con recruiter: ahí el rango con contexto (remoto, bonus, horario). Por escrito antes de hablar con humano, el número es munición en contra tuya. Si la oferta publica banda salarial (cada vez más común en UE), alinea tu expectativa con ese rango sin copiarlo al PDF.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo “salario a convenir”?',
              a: 'En el PDF casi nunca aporta. En formularios con texto libre, mejor un rango estrecho que una frase vacía.',
            },
            {
              q: '¿Y si llevo años cobrando poco?',
              a: 'No declares tu salario actual en el CV. Negocia por valor de puesto, no por historial.',
            },
            {
              q: '¿En Latinoamérica cambia?',
              a: 'En algunos países piden expectativa más pronto. Misma lógica: portal sí, PDF solo si lo piden explícitamente.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Salary expectations on a CV: when to include them and when not to',
      description:
        'Should you put salary or pay expectations on your résumé? When it helps, when it gets you filtered out, and where to write it if the portal requires it.',
      readMinutes: 11,
      sections: [
        {
          type: 'p',
          text: 'Salary expectations on a CV cause two opposite mistakes: a random number “to be safe”, or hiding it when the form requires it and leaving the field blank. In Spain and most of Western Europe, salary on the résumé document is not mandatory and often hurts: it anchors you low if you asked little, filters you out if high, and you negotiate without market context. This guide covers when it belongs on the PDF, when only on the portal, and when nowhere.',
        },
        {
          type: 'p',
          text: 'Complements [what not to put on a CV](/blog/que-no-poner-en-el-cv). When tailoring applications, salary rarely changes match — keywords do.',
        },
        {
          type: 'p',
          text: 'ClarCV does not fill salary fields. It fixes the file so ATS can read it. Free: 3 analyses / 30 days, no card.',
        },
        { type: 'h2', text: 'General rule: off the PDF' },
        {
          type: 'p',
          text: 'On the résumé document (PDF or Word) you usually do not put bare “€30,000 gross”. It ages badly and does not separate fixed from variable pay. If the portal has a salary field, answer there with a researched range — not in the CV header.',
        },
        { type: 'h2', text: 'When the portal or staffing firm requires it' },
        {
          type: 'p',
          text: 'Randstad, Adecco, InfoJobs, and some corporate ATS ask for a range in the form. Do not copy that number into the PDF. Use a range from public sources, rounded. “Negotiable depending on package” works when the field allows free text.',
        },
        { type: 'h2', text: 'When it MAY go on the CV (rare)' },
        {
          type: 'p',
          text: 'Unsolicited application to a small company without a structured portal and they explicitly asked for “CV with salary expectation”. One line under contact: “Expectation: €28–32k gross/year, negotiable”.',
        },
        { type: 'h2', text: 'Mistakes that get you filtered' },
        {
          type: 'ul',
          items: [
            'Current salary on the CV when you want more — it boxes you in.',
            '“Per collective agreement” in sectors with no clear agreement.',
            'Double market rate without seniority to support it.',
            'Mixing net and gross without saying which.',
            'Salary in the professional summary — first thing read, first filter applied.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Salary does not fix a PDF nobody reads',
          lead: 'If you worry about the filter, first check the file is readable. Then negotiate from a CV that reaches HR.',
          from: 'CV with wrong number placement',
          to: 'Clean file + range only where required',
          button: 'Review my CV free',
        },
        { type: 'h2', text: 'Where to negotiate instead' },
        {
          type: 'p',
          text: 'Phone screen or first recruiter round: range with context (remote, bonus, hours). In writing before a human conversation, the number works against you. If the ad publishes a band (increasingly common in the EU), align expectations without copying it into the PDF.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I write “salary negotiable”?',
              a: 'On the PDF it rarely adds value. On forms with free text, a narrow range beats an empty phrase.',
            },
            {
              q: 'What if I have been underpaid for years?',
              a: 'Do not declare current salary on the CV. Negotiate on role value, not history.',
            },
            {
              q: 'Does Latin America differ?',
              a: 'Some countries ask for expectations earlier. Same logic: portal yes, PDF only if explicitly requested.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
